import { create } from 'zustand';

/**
 * NavRail Store Requirements & Invariants
 * 
 * State Management:
 * 1. Active Item Management:
 *    - Active item is determined solely by URL matching
 *    - Uses longest matching prefix for URL matching
 *    - Only one nav item can be active at a time
 * 
 * 2. User Preferences:
 *    - Track whether user prefers panels to be open
 *    - Preference persists across navigation
 * 
 * 3. State Transitions:
 *    - On path change:
 *      - Update active item based on current route
 *      - Clear hover states
 *    - On hover state change:
 *      - Update currently hovered item (including active items)
 *      - Update hover sidenav if item has one AND is not active
 *      - Active items can be hovered but never show sidenav
 *    - On panel toggle:
 *      - Update user preference
 */

export interface NavItemConfig {
  id: string;
  path: string;
  hasSidenav?: boolean;
}

interface NavRailStateData {
  currentPath: string;
  userPrefersPanelOpen: boolean;
  hoveredItemId: string | null;
  hoverSidenavId: string | null;
  navItems: NavItemConfig[];
}

interface NavRailState extends NavRailStateData {
  // Computed
  activeNavItemId: string | null;

  // Actions
  setPath: (path: string) => void;
  togglePanel: () => void;
  setHoveredItem: (itemId: string | null, immediate?: boolean) => void;
  setNavItems: (items: NavItemConfig[]) => void;
}

export interface FindActiveNavItemArgs {
  currentPath: string;
  items: NavItemConfig[];
}

/**
 * Find the best matching nav item for a given path by matching longest input URL segments first
 */
export function findActiveNavItem({ currentPath, items }: FindActiveNavItemArgs): NavItemConfig | undefined {
  // Remove query parameters for matching
  const pathWithoutQuery = currentPath.split('?')[0];

  // Find all matching items and sort by path length (longest first)
  const matches = items
    .filter(item => {
      // Must start with the item path
      if (!pathWithoutQuery.startsWith(item.path)) return false;
      
      // And must end at a path boundary (end of string or followed by /)
      return pathWithoutQuery.length === item.path.length || 
             pathWithoutQuery[item.path.length] === '/';
    })
    .sort((a, b) => b.path.length - a.path.length);

  // Return the most specific match (longest path) or undefined if no matches
  return matches[0];
}

export const createNavRailStore = (initialState: NavRailStateData) => {
  // Calculate initial active item
  const initialActiveItem = findActiveNavItem({
    currentPath: initialState.currentPath,
    items: initialState.navItems
  });

  let hoverTimeoutId: number | null = null;

  return create<NavRailState>((set) => ({
    ...initialState,
    activeNavItemId: initialActiveItem?.id ?? null,

    setPath: (path: string) => 
      set(state => {
        const activeItem = findActiveNavItem({
          currentPath: path,
          items: state.navItems
        });
        
        // Clear any pending hover timeout
        if (hoverTimeoutId !== null) {
          clearTimeout(hoverTimeoutId);
          hoverTimeoutId = null;
        }

        return {
          ...state,
          currentPath: path,
          activeNavItemId: activeItem?.id ?? null,
          hoveredItemId: null,
          hoverSidenavId: null
        };
      }),

    togglePanel: () => 
      set(state => ({ 
        ...state,
        userPrefersPanelOpen: !state.userPrefersPanelOpen
      })),

    setHoveredItem: (itemId: string | null, immediate = false) => 
      set(state => {
        // Clear any pending timeout
        if (hoverTimeoutId !== null) {
          clearTimeout(hoverTimeoutId);
          hoverTimeoutId = null;
        }

        // If immediate or setting a new hover item, update state now
        if (immediate || itemId !== null) {
          // Find the nav item if it exists
          const navItem = itemId ? state.navItems.find(item => item.id === itemId) : null;
          
          return {
            ...state,
            hoveredItemId: itemId,
            // Only show sidenav if item has one AND is not the active item
            hoverSidenavId: navItem?.hasSidenav && itemId !== state.activeNavItemId ? itemId : null
          };
        }

        // Otherwise set a timeout to clear hover state
        hoverTimeoutId = window.setTimeout(() => {
          set(state => ({
            ...state,
            hoveredItemId: null,
            hoverSidenavId: null
          }));
          hoverTimeoutId = null;
        }, 150);

        // Don't update state yet
        return state;
      }),

    setNavItems: (items: NavItemConfig[]) =>
      set(state => {
        const activeItem = findActiveNavItem({
          currentPath: state.currentPath,
          items
        });

        return {
          ...state,
          navItems: items,
          activeNavItemId: activeItem?.id ?? null,
          hoveredItemId: null,
          hoverSidenavId: null
        };
      })
  }));
};

// Create the default store instance with empty initial state
export const useNavRailStore = createNavRailStore({
  currentPath: '/',
  userPrefersPanelOpen: true,
  hoveredItemId: null,
  hoverSidenavId: null,
  navItems: []
}); 